import React, { Fragment, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import HeaderComponent from "../../components/HeaderComponent";
import TitleH2Component from "../../components/TitleH2Component";
import TitleH4Component from "../../components/TitleH4Component";
import InputComponent from "../../components/InputComponent";
import SelectComponent from "../../components/SelectComponent";
import ParrafoComponent from "../../components/ParrafoComponent";
import PaginadorComponent from "../../components/PaginadorComponent";
import LoaderComponent from "../../components/LoaderComponent";
import AlertComponent from "../../components/AlertComponent";
import ModalGeneralComponent from "../../components/ModalGeneralComponent";
import EstadoAlquilerComponent from "../../components/EstadoAlquilerComponent";
import { Icon } from "@iconify/react/dist/iconify.js";
import stylebtn from "../../styles/btn.module.scss";
import stylegenral from "../../styles/general.module.scss";
import { getAlquileres, editarAlquiler, eliminarAlquiler } from "../../api/Alquiler";
import { useAuth } from "../../context/AuthContext";
import { useFormatter } from "../../hooks/useFormatter";

const optionEstado = [
  { value: "activo", label: "ACTIVO" },
  { value: "vencido", label: "VENCIDO" },
  { value: "devuelto", label: "DEVUELTO" },
];

const AlquileresPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isSuper } = useAuth();
  const { formatDateHora, formatPrice, formatDateTimeInput } = useFormatter();

  const [loading, setLoading] = useState(false);
  const [alquileres, setAlquileres] = useState([]);
  const [mensaje, setMensaje] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [busqueda, setBusqueda] = useState("");
  const [estadoF, setEstadoF] = useState("");
  const [fechaBusqueda, setFechaBusqueda] = useState({ fechaI: "", fechaFin: "" });

  // ── Editar (solo super) ──
  const [showEditar, setShowEditar] = useState(false);
  const [alquilerEditar, setAlquilerEditar] = useState(null);
  const [formEditar, setFormEditar] = useState({
    nombre: "",
    cedula: "",
    telefono: "",
    fecha_hora_salida: "",
    fecha_hora_devolucion_esperada: "",
    observaciones: "",
  });
  const [mensajeEditar, setMensajeEditar] = useState("");
  const [alert, setAlert] = useState(false);
  const [mensajealerta, setMensajealerta] = useState("");

  // ── Eliminar (solo super) ──
  const [showEliminar, setShowEliminar] = useState(false);
  const [alquilerEliminar, setAlquilerEliminar] = useState(null);
  const [mensajeEliminar, setMensajeEliminar] = useState("");

  const itemsPerPage = 7;
  const pageCount = Math.ceil(alquileres.length / itemsPerPage);
  const currentPageData = alquileres.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage,
  );

  const getSelectedValue = (selected) =>
    selected && typeof selected === "object" ? selected.value : selected ?? "";

  const getFiltersFromSearch = (search) => {
    const params = new URLSearchParams(search);
    return {
      query: params.get("search") || "",
      estado: params.get("estado") || "",
      fechaI: params.get("fechaI") || "",
      fechaFin: params.get("fechaFin") || "",
    };
  };

  const fetchAlquileres = async (filtros) => {
    setLoading(true);
    try {
      const { status, res } = await getAlquileres({
        searchQuery: filtros.query,
        estado: filtros.estado || undefined,
        startDate: filtros.fechaI || undefined,
        endDate: filtros.fechaFin || undefined,
      });
      if (status === 200) {
        setAlquileres(res?.data || []);
        setMensaje("");
      } else {
        // Antes esto fallaba en silencio: si el backend respondía con error
        // (o no respondía) la tabla solo decía "SIN ALQUILERES" sin ninguna
        // pista de qué pasó.
        setAlquileres([]);
        setMensaje(res?.msg || "No fue posible cargar los alquileres");
      }
    } catch (error) {
      console.error("Error cargando alquileres", error);
      setMensaje("No fue posible conectar con el servidor");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const filtros = getFiltersFromSearch(location.search);
    setBusqueda(filtros.query);
    setEstadoF(filtros.estado);
    setFechaBusqueda({ fechaI: filtros.fechaI, fechaFin: filtros.fechaFin });
    fetchAlquileres(filtros);
  }, [location.search]);

  // Filtra automáticamente apenas se elige un valor, sin esperar al botón
  // "Buscar" — mismo patrón ya corregido en Ventas Realizadas: los overrides
  // puntuales evitan depender del estado de React que aún no se actualizó.
  const aplicarFiltros = (overrides = {}) => {
    const {
      queryVal = busqueda,
      estadoVal = estadoF,
      fechaIVal = fechaBusqueda.fechaI,
      fechaFinVal = fechaBusqueda.fechaFin,
    } = overrides;

    const params = new URLSearchParams();
    if (queryVal) params.set("search", queryVal);
    if (estadoVal) params.set("estado", String(estadoVal));
    if (fechaIVal) params.set("fechaI", fechaIVal);
    if (fechaFinVal) params.set("fechaFin", fechaFinVal);

    setCurrentPage(0);
    navigate(`?${params.toString()}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    aplicarFiltros();
  };

  const handleChangeFecha = (e) => {
    const { name, value } = e.target;
    const nueva = { ...fechaBusqueda, [name]: value };
    setFechaBusqueda(nueva);
    aplicarFiltros({ fechaIVal: nueva.fechaI, fechaFinVal: nueva.fechaFin });
  };

  const limpiarFiltros = () => {
    setBusqueda("");
    setEstadoF("");
    setFechaBusqueda({ fechaI: "", fechaFin: "" });
    setCurrentPage(0);
    navigate("");
  };

  // ── Editar alquiler (solo super) ──
  const handleShowEditar = (alq) => {
    setAlquilerEditar(alq);
    setFormEditar({
      nombre: alq?.nombre || "",
      cedula: alq?.cedula || "",
      telefono: alq?.telefono || "",
      fecha_hora_salida: formatDateTimeInput(alq?.fecha_hora_salida),
      fecha_hora_devolucion_esperada: alq?.fecha_hora_devolucion_esperada
        ? formatDateTimeInput(alq.fecha_hora_devolucion_esperada)
        : "",
      observaciones: alq?.observaciones || "",
    });
    setMensajeEditar("");
    setShowEditar(true);
  };

  const handleCloseEditar = () => {
    setShowEditar(false);
    setAlquilerEditar(null);
    setMensajeEditar("");
  };

  const handleChangeEditar = (e) => {
    const { name, value } = e.target;
    setFormEditar((prev) => ({ ...prev, [name]: value }));
  };

  const confirmarEditar = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { status, res } = await editarAlquiler(alquilerEditar.id, formEditar);

      if (status === 200 && res?.success) {
        handleCloseEditar();
        setAlert(true);
        setMensajealerta(res.msg);
        fetchAlquileres(getFiltersFromSearch(location.search));
      } else {
        setMensajeEditar(res?.msg || "No fue posible actualizar el alquiler");
      }
    } catch (error) {
      setMensajeEditar("No fue posible actualizar el alquiler");
    } finally {
      setLoading(false);
      setTimeout(() => setAlert(false), 4000);
    }
  };

  // ── Eliminar alquiler (solo super) ──
  const handleShowEliminar = (alq) => {
    setAlquilerEliminar(alq);
    setMensajeEliminar("");
    setShowEliminar(true);
  };

  const handleCloseEliminar = () => {
    setShowEliminar(false);
    setAlquilerEliminar(null);
    setMensajeEliminar("");
  };

  const confirmarEliminar = async () => {
    try {
      setLoading(true);
      const { status, res } = await eliminarAlquiler(alquilerEliminar.id);

      if (status === 200 && res?.success) {
        handleCloseEliminar();
        setAlert(true);
        setMensajealerta(res.msg);
        fetchAlquileres(getFiltersFromSearch(location.search));
      } else {
        setMensajeEliminar(res?.msg || "No fue posible eliminar el alquiler");
      }
    } catch (error) {
      setMensajeEliminar("No fue posible eliminar el alquiler");
    } finally {
      setLoading(false);
      setTimeout(() => setAlert(false), 4000);
    }
  };

  return (
    <Fragment>
      <div className="d-flex justify-content-center">
        <AlertComponent
          styleGroup="bg-soccess color-white"
          mensaje={mensajealerta}
          activaalert={alert}
        />
      </div>
      <HeaderComponent>
        <TitleH2Component styleGroup="box-text color-dark fw700 roboto mb-4">
          ALQUILERES
        </TitleH2Component>
        <Link
          to="/alquileres/nuevo"
          className={`${stylebtn.btn} ${stylebtn.primary} ${stylebtn.btnMovil}`}
        >
          NUEVO ALQUILER
        </Link>
      </HeaderComponent>

      <div className="conatiner-global pt-3 mb-5">
        <section>
          <div className="container">
            <form action="" onSubmit={handleSubmit}>
              <div className="row gy-4">
                <div className="col-12 col-md-6 col-xl-3">
                  <SelectComponent
                    label="Estado"
                    value={
                      optionEstado.find(
                        (o) => String(o.value) === String(estadoF),
                      ) || null
                    }
                    capOpcions={(selected) => {
                      const value = getSelectedValue(selected);
                      setEstadoF(value);
                      aplicarFiltros({ estadoVal: value });
                    }}
                    options={optionEstado}
                    styleGroup={`${stylegenral.wselect}`}
                    styleLabel="fw600 WorkSans color-madera"
                    customPlaceholder="Estado"
                    isClearable
                  />
                </div>
                <div className="col-12 col-md-6 col-xl-3">
                  <InputComponent
                    label="Fecha Inicio:"
                    styleLabel="fw600 WorkSans"
                    type="date"
                    name="fechaI"
                    value={fechaBusqueda.fechaI}
                    onChange={handleChangeFecha}
                  />
                </div>
                <div className="col-12 col-md-6 col-xl-3">
                  <InputComponent
                    label="Fecha Final:"
                    styleLabel="fw600 WorkSans"
                    type="date"
                    name="fechaFin"
                    value={fechaBusqueda.fechaFin}
                    onChange={handleChangeFecha}
                  />
                </div>
                <div className="col-12 col-md-6 col-xl-3">
                  <InputComponent
                    placeholder="Nombre o cédula"
                    label="Buscar cliente"
                    styleLabel="fw600 WorkSans"
                    type="search"
                    name="busqueda"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                  />
                </div>
                <div className="col-12 col-md-6 col-xl-2 d-flex align-items-end">
                  <button
                    type="submit"
                    className={`${stylebtn.btn} ${stylebtn.primary}`}
                  >
                    BUSCAR
                  </button>
                </div>
                <div className="col-12 col-md-6 col-xl-2 d-flex align-items-end">
                  <button
                    type="button"
                    className={`${stylebtn.btn} ${stylebtn.secundary}`}
                    onClick={limpiarFiltros}
                  >
                    Limpiar filtros
                  </button>
                </div>
              </div>
            </form>
          </div>
        </section>

        {mensaje && (
          <ParrafoComponent styleGroup="text-center color-danger fw400 WorkSans mt-3">
            {mensaje}
          </ParrafoComponent>
        )}

        {loading ? (
          <LoaderComponent styleGroup="mt-5" />
        ) : (
          <section className="mt-4">
            <div className="table-responsive">
              <table className="table table-bordered tableGeneral">
                <thead>
                  <tr className="bg-light border-bottom">
                    <th className="color-madera py-3 text-center" style={{ width: "50px" }}>
                      #
                    </th>
                    <th className="color-madera py-3">Cliente</th>
                    <th className="color-madera py-3">Documento</th>
                    <th className="color-madera py-3 text-center">Salida</th>
                    <th className="color-madera py-3 text-center">Devolución esperada</th>
                    <th className="color-madera py-3 text-end">Total</th>
                    <th className="color-madera py-3 text-center">Estado</th>
                    <th className="color-madera py-3 text-center" style={{ width: "60px" }}>
                      Ver
                    </th>
                    {isSuper && (
                      <>
                        <th className="color-madera py-3 text-center" style={{ width: "60px" }}>
                          Editar
                        </th>
                        <th className="color-madera py-3 text-center" style={{ width: "60px" }}>
                          Eliminar
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                {alquileres.length > 0 ? (
                  <tbody>
                    {currentPageData.map((alq, index) => (
                      <tr key={alq.id}>
                        <th>{currentPage * itemsPerPage + index + 1}</th>
                        <td>
                          {alq?.nombre ||
                            `${alq?.Cliente?.Persona?.nombre || ""} ${alq?.Cliente?.Persona?.apellido || ""}`}
                        </td>
                        <td>{alq?.cedula || alq?.Cliente?.Persona?.cedula}</td>
                        <td className="text-center">
                          {formatDateHora(alq?.fecha_hora_salida)}
                        </td>
                        <td className="text-center">
                          {alq?.fecha_hora_devolucion_esperada
                            ? formatDateHora(alq?.fecha_hora_devolucion_esperada)
                            : "-"}
                        </td>
                        <td className="text-end">{formatPrice(alq?.total)}</td>
                        <EstadoAlquilerComponent
                          estado={alq?.estado}
                          fechaHoraDevolucionEsperada={alq?.fecha_hora_devolucion_esperada}
                        />
                        <td className="text-center">
                          <Link
                            to={`/alquileres/${alq?.id}`}
                            className={`${stylebtn.btn_desnudo} ${stylebtn.icon} p-0 d-inline-flex`}
                          >
                            <Icon icon="mdi:file-search-outline" />
                          </Link>
                        </td>
                        {isSuper && (
                          <>
                            <td className="text-center">
                              <button
                                type="button"
                                className={`${stylebtn.btn_desnudo} ${stylebtn.icon} p-0 d-inline-flex`}
                                onClick={() => handleShowEditar(alq)}
                              >
                                <Icon icon="raphael:edit" />
                              </button>
                            </td>
                            <td className="text-center">
                              <button
                                type="button"
                                className={`${stylebtn.btn_desnudo} ${stylebtn.icon} p-0 d-inline-flex color-danger`}
                                onClick={() => handleShowEliminar(alq)}
                              >
                                <Icon icon="majesticons:delete-bin" />
                              </button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                ) : (
                  <tbody>
                    <tr>
                      <td colSpan={isSuper ? 10 : 8} className="text-center">
                        SIN ALQUILERES
                      </td>
                    </tr>
                  </tbody>
                )}
              </table>
            </div>
            {pageCount > 1 && (
              <PaginadorComponent
                setCurrentPage={setCurrentPage}
                pages={pageCount}
                styleGroup="d-flex justify-content-center pt-4"
              />
            )}
          </section>
        )}
      </div>

      <ModalGeneralComponent
        ModalHeader
        titulo="Editar alquiler"
        styleGroup="d-flex align-items-center modal-general"
        styleTitle="box-text color-madera fw700 roboto text-center WorkSans"
        show={showEditar}
        handleClose={handleCloseEditar}
      >
        <form onSubmit={confirmarEditar}>
          <ParrafoComponent styleGroup="text-center color-danger fw400 WorkSans mb-2">
            {mensajeEditar}
          </ParrafoComponent>

          <div className="row gy-3 mb-4">
            <div className="col-12 col-md-6">
              <InputComponent
                label="Nombre"
                styleLabel="fw600 WorkSans"
                type="text"
                name="nombre"
                value={formEditar.nombre}
                onChange={handleChangeEditar}
              />
            </div>
            <div className="col-12 col-md-6">
              <InputComponent
                label="Cédula"
                styleLabel="fw600 WorkSans"
                type="text"
                name="cedula"
                value={formEditar.cedula}
                onChange={handleChangeEditar}
              />
            </div>
            <div className="col-12 col-md-6">
              <InputComponent
                label="Teléfono"
                styleLabel="fw600 WorkSans"
                type="text"
                name="telefono"
                value={formEditar.telefono}
                onChange={handleChangeEditar}
              />
            </div>
            <div className="col-12 col-md-6">
              <InputComponent
                label="Fecha y hora de salida"
                styleLabel="fw600 WorkSans"
                type="datetime-local"
                name="fecha_hora_salida"
                value={formEditar.fecha_hora_salida}
                onChange={handleChangeEditar}
              />
            </div>
            <div className="col-12 col-md-6">
              <InputComponent
                label="Fecha y hora de devolución esperada"
                styleLabel="fw600 WorkSans"
                type="datetime-local"
                name="fecha_hora_devolucion_esperada"
                value={formEditar.fecha_hora_devolucion_esperada}
                onChange={handleChangeEditar}
              />
            </div>
            <div className="col-12">
              <InputComponent
                label="Observaciones"
                styleLabel="fw600 WorkSans"
                type="text"
                name="observaciones"
                value={formEditar.observaciones}
                onChange={handleChangeEditar}
              />
            </div>
          </div>

          <div className="d-flex w-100 justify-content-end">
            <button
              type="button"
              className={`${stylebtn.btn_desnudo} ${stylebtn.line} p-0 me-3`}
              onClick={handleCloseEditar}
            >
              CANCELAR
            </button>
            <button type="submit" className={`btn ${stylebtn.btn} ${stylebtn.primary}`}>
              GUARDAR
            </button>
          </div>
        </form>
      </ModalGeneralComponent>

      <ModalGeneralComponent
        styleGroup="d-flex align-items-center modal-general"
        styleTitle="box-text color-madera fw700 roboto text-center WorkSans"
        show={showEliminar}
        handleClose={handleCloseEliminar}
      >
        <TitleH4Component styleGroup="box-text color-madera fw700 roboto mb-2 text-center">
          ¿Seguro que quiere eliminar este alquiler?
        </TitleH4Component>
        <ParrafoComponent styleGroup="box-text color-gris fw400 WorkSans mb-2 text-center">
          Solo es posible si todavía no tiene ninguna devolución registrada; el
          inventario descontado se restaura por completo.
        </ParrafoComponent>
        {mensajeEliminar && (
          <ParrafoComponent styleGroup="text-center color-danger fw400 WorkSans mb-2">
            {mensajeEliminar}
          </ParrafoComponent>
        )}
        <div className="d-flex justify-content-end">
          <button
            type="button"
            onClick={handleCloseEliminar}
            className={`btn ${stylebtn.btn} ${stylebtn.danger}`}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={`btn ${stylebtn.btn} ${stylebtn.primary} ms-3`}
            onClick={confirmarEliminar}
          >
            SI
          </button>
        </div>
      </ModalGeneralComponent>
    </Fragment>
  );
};

export default AlquileresPage;
